import { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Type } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { getCurrentUser } from '../utils/auth';
import { postAPI } from '../services/api';
import { toast } from 'sonner';

const CreateStory = ({ onClose, onStoryCreated }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Please select an image or video file');
      return;
    }

    // Check file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File is too large (max 50MB)');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);

    try {
      // Upload media file
      console.log('Uploading story media:', selectedFile.name);
      const uploadResponse = await postAPI.uploadMedia(selectedFile);
      console.log('Story upload response:', uploadResponse.data);
      
      const mediaUrl = uploadResponse.data.mediaUrl;
      const mediaType = uploadResponse.data.mediaType;

      // Create story post with special tag
      const response = await postAPI.createPost('', [mediaUrl], ['story']);
      console.log('Story created:', response.data);
      
      toast.success('Story posted successfully!');
      
      if (onStoryCreated) {
        onStoryCreated(response.data.post);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating story:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create story';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-card rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-foreground">Create Story</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 overflow-y-auto flex-1">
          {!previewUrl ? (
            <div className="space-y-4">
              <p className="text-muted-foreground text-center mb-4">
                Share a moment with your story
              </p>
              
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <div className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary transition-colors">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-foreground font-medium">
                    Click to upload photo or video
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Max file size: 50MB
                  </p>
                </div>
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview */}
              <div className="relative bg-black rounded-lg overflow-hidden">
                {selectedFile?.type.startsWith('video/') ? (
                  <video
                    src={previewUrl}
                    className="w-full max-h-[60vh] object-contain"
                    controls
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Story preview"
                    className="w-full max-h-[60vh] object-contain"
                  />
                )}
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                  }}
                  className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="flex-1 text-white"
                  className="btn-primary"
                >
                  {uploading ? 'Posting...' : 'Share to Story'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CreateStory;
